import React, { useCallback, useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { Grid } from '@mui/material';
import useAbbreviationSettings from 'api/useAbbreviationSettings';
import { AxiosError } from 'axios';
import { systemTexts } from 'constants/localization';
import { FormikHelpers, useFormik } from 'formik';

import ActionButton from 'components/ActionButton/ActionButton';
import { Flex } from 'components/ui/flex';
import { Separator } from 'components/ui/separator';

import { EToasterStatus, showToast } from '../../../utils/showToast';

import { toWirePayload } from './components/AbbreviationsSettingsV2/AbbreviationsSettingsV2.payload';
import { QuantityFormatCard } from './components/QuantityFormatCard';
import { useStoreSettingsInitialValues } from './hooks/useStoreSettingsInitialValues';
import { QUANTITY_FORMAT_SECTIONS } from './StoreSettings.consts';
import { IStoreSettingsFormValues } from './StoreSettings.types';
import { applyQuantityFieldChange } from './StoreSettings.utils';
import { storeSettingsSchema } from './StoreSettings.validation';

import '../../../style/forms.scss';
import '../../Theme/style.scss';

export const StoreSettingsLegacy = (): React.ReactElement => {
  const { publisherId } = useParams();
  const { getAbbreviationSettings, updateAbbreviationSettings } =
    useAbbreviationSettings(publisherId);
  const { initialValues } = useStoreSettingsInitialValues();

  const handleSubmit = useCallback(
    async (
      values: IStoreSettingsFormValues,
      helpers: FormikHelpers<IStoreSettingsFormValues>
    ): Promise<void> => {
      try {
        await updateAbbreviationSettings.mutateAsync(
          toWirePayload(values.numberFormat)
        );
        showToast({
          message: systemTexts.settingsTexts.THEME_UPDATED,
          status: EToasterStatus.SUCCESS
        });
        helpers.setSubmitting(false);
      } catch (error) {
        showToast({
          message: systemTexts.settingsTexts.THEME_UPDATE_ERROR,
          status: EToasterStatus.ERROR,
          description:
            ((error as AxiosError).response?.data as { message: string })
              .message || 'Unknown error occurred'
        });
      }
    },
    [updateAbbreviationSettings]
  );

  const formik = useFormik<IStoreSettingsFormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema: storeSettingsSchema,
    onSubmit: handleSubmit
  });

  const handleFieldChange = useCallback(
    (fieldPath: string, rawValue: string): void => {
      applyQuantityFieldChange(formik.setFieldValue, fieldPath, rawValue);
    },
    [formik.setFieldValue]
  );

  const sections = useMemo(
    () =>
      QUANTITY_FORMAT_SECTIONS.map((section, index) => (
        <React.Fragment key={section.key}>
          {index > 0 && <Separator className="my-6 bg-base-border" />}
          <QuantityFormatCard
            title={section.title}
            name={section.key}
            quantityFormat={formik.values.numberFormat[section.key].quantity}
            isFetching={getAbbreviationSettings.isFetching}
            touched={formik.touched.numberFormat?.[section.key]?.quantity}
            errors={formik.errors.numberFormat?.[section.key]?.quantity}
            onFieldChange={handleFieldChange}
            onBlur={formik.handleBlur}
          />
        </React.Fragment>
      )),
    [
      formik.values,
      formik.touched,
      formik.errors,
      formik.handleBlur,
      getAbbreviationSettings.isFetching,
      handleFieldChange
    ]
  );

  return (
    <Grid container className="formContent">
      <Grid item>
        <Flex>{sections}</Flex>
        <Grid item xs={6} mt={3} mb={3}>
          <ActionButton
            variant="outlined"
            text={systemTexts.actionTexts.UPDATE}
            onClick={formik.submitForm}
            disabled={!formik.isValid || !formik.dirty || formik.isSubmitting}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
